import { HashIcon } from "lucide-react";
import { useCallback } from "react";
import { T, RecordProps, TLShape, TLAsset, TLBinding, Editor } from "tldraw";

import {
  BlockBody,
  BlockContainer,
  NodeBlockHeader,
  BlockContainerShapeContext,
} from "@/components/canvas-editor/components/Container";
import {
  CanvasPercentageInput,
  CanvasSimpleSelect,
  PercentOperatorSelect,
} from "@/components/canvas-editor/components/Inputs";
import {
  modelProbabilityExplanation,
  NodeDocs,
} from "@/components/canvas-editor/NodeDocs";
import {
  BindableUtil,
  createMigrations,
} from "@/components/canvas-editor/shapes/bindable";
import {
  TopicAnalysisShape,
  topicAnalysisType,
  comparisonOperators,
  ComparisonOperator,
  TopicAnalysisProps,
  topicAnalysisCategories,
  TopicAnalysisCategory,
  metadataType,
  Dimensions,
} from "@/components/canvas-editor/shapes/shapes";
import { MIN_H, ToolStateNode } from "@/components/canvas-editor/utils";
import { SimpleTooltip } from "@/components/ui/tooltip";

export function isTopicAnalysisShape(
  shape: TLAsset | TLBinding | TLShape,
): shape is TopicAnalysisShape {
  return shape.type === topicAnalysisType;
}

export class TopicAnalysisTool extends ToolStateNode {
  static override id = topicAnalysisType;

  static override uiToolDef = (editor: Editor) => ({
    id: topicAnalysisType,
    icon: HashIcon,
    label: TopicAnalysisShapeUtil.docs.title,
    defaultColor: TopicAnalysisShapeUtil.docs.defaultColor,
    onSelect: () => editor.setCurrentTool(topicAnalysisType),
    docs: () => (
      <NodeDocs
        editor={editor}
        component={TopicAnalysis}
        util={TopicAnalysisShapeUtil}
      />
    ),
  });

  override onEnter() {
    this.editor.setCursor({ type: "none", rotation: 0 });
  }

  override onPointerDown() {
    const { currentPagePoint } = this.editor.inputs;

    this.editor.createShape({
      type: topicAnalysisType,
      x: currentPagePoint.x,
      y: currentPagePoint.y,
    });

    this.editor.setCurrentTool("select");
  }
}

export class TopicAnalysisShapeUtil extends BindableUtil<TopicAnalysisShape> {
  static override migrations = createMigrations(topicAnalysisType);
  static override type = topicAnalysisType;

  static override docs = {
    defaultColor: "blue" as const,
    title: "Topic Analysis",
    description:
      "Use AI to analyze the main topic of the content. This detects various categories from arts and culture to technology and sports.",
  };

  override getType() {
    return topicAnalysisType;
  }

  static override props: RecordProps<TopicAnalysisShape> = {
    w: T.number,
    minW: T.number,
    isCollapsed: T.boolean.optional(),
    resultState: T.literalEnum("success", "failure", "idle").optional(),
    isReadOnly: T.boolean.optional(),
    h: T.number,
    topicAnalysisCategory: T.literalEnum(...topicAnalysisCategories),
    operator: T.literalEnum(...comparisonOperators),
    value: T.or(T.number, T.string),
    metadata: metadataType,
  };

  override getDefaultProps() {
    return {
      w: 440,
      minW: 440,
      h: MIN_H,
      topicAnalysisCategory: "Science & Technology" as const,
      operator: ">=" as const,
      value: 0.5,
      metadata: undefined,
    };
  }

  override component(shape: TopicAnalysisShape) {
    const selected = this.editor.getSelectedShapeIds();
    const isSelected = selected.includes(shape.id);

    return (
      <TopicAnalysis
        shape={shape}
        updateShape={this.updateProps.bind(this)}
        isBound={this.getBoundToFeed(shape)}
        isSelected={isSelected}
      />
    );
  }
}

function TopicAnalysis({
  isBound,
  shape,
  className,
  updateShape,
  isSelected,
}: {
  isSelected: boolean;
  isBound: boolean;
  shape: TopicAnalysisShape;
  className?: string;
  updateShape: (
    shape: TopicAnalysisShape,
    props: Partial<TopicAnalysisProps>,
  ) => void;
}) {
  const onUpdateDimensions = useCallback(
    (dimensions: Pick<Dimensions, "h">) => {
      return updateShape(shape, dimensions);
    },
    [shape, updateShape],
  );

  return (
    <BlockContainerShapeContext value={shape}>
      <BlockContainer
        resultState={shape.props.resultState}
        pattern={shape.props.metadata?.pattern}
        isBound={isBound}
        className={className}
        variant={
          shape.props.metadata?.color ||
          TopicAnalysisShapeUtil.docs.defaultColor
        }
        isSelected={isSelected}
        onUpdateDimensions={onUpdateDimensions}
        dynamicHeight={true}
      >
        <NodeBlockHeader
          props={shape.props}
          docs={TopicAnalysisShapeUtil.docs}
        />
        <BlockBody>
          <CanvasSimpleSelect
            aria-label="Topic Analysis Category"
            options={topicAnalysisCategories}
            value={shape.props.topicAnalysisCategory}
            className="shrink-0"
            onValueChange={(v) =>
              updateShape(shape, {
                topicAnalysisCategory: v as TopicAnalysisCategory,
              })
            }
            tooltip={TopicAnalysisShapeUtil.docs.description}
          />
          <PercentOperatorSelect
            value={shape.props.operator}
            onValueChange={(v) =>
              updateShape(shape, { operator: v as ComparisonOperator })
            }
          />
          <SimpleTooltip message={modelProbabilityExplanation}>
            <CanvasPercentageInput
              aria-label="Threshold"
              value={shape.props.value as number}
              className="flex-1"
              placeholder="Threshold"
              onValueChange={(value) => updateShape(shape, { value })}
            />
          </SimpleTooltip>
        </BlockBody>
      </BlockContainer>
    </BlockContainerShapeContext>
  );
}
